// src/components/auth/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { msalInstance, loginRequest } from '../../config/msalConfig';
import Logo from '../common/Logo';
import { Button, Field, IconButton, TextInput } from '../common/ui';

const MicrosoftLogo = () => (
  <svg className="h-4 w-4" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Login = () => {
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [error, setError] = useState(() => {
    // index.jsx stashes the reason a Microsoft redirect bounced, since the
    // page reloads before React can hold onto it.
    try {
      const stored = localStorage.getItem('microsoftLoginError');
      if (stored) localStorage.removeItem('microsoftLoginError');
      return stored || '';
    } catch {
      return '';
    }
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', form);
      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Couldn't reach the server. Check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setIsMicrosoftLoading(true);
    setError('');
    msalInstance.loginRedirect(loginRequest).catch(() => {
      setError('Microsoft sign-in could not start. Try again, or use your password.');
      setIsMicrosoftLoading(false);
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex flex-col items-center text-center">
          <Logo variant="vertical" className="mb-5 h-[84px]" />
          <h1 className="text-xl font-semibold">AI Portal</h1>
          <p className="mt-1.5 text-sm text-ink-2">
            Sign in to reach your team's AI tools.
          </p>
        </div>

        <div className="rounded-xl border border-line bg-surface p-6 shadow-card">
          {error && (
            <p className="mb-5 rounded-lg bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
              {error}
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Field label="Username" htmlFor="login-username">
              <TextInput
                id="login-username"
                value={form.username}
                onChange={(e) => set({ username: e.target.value })}
                autoComplete="username"
                autoFocus
                required
              />
            </Field>

            <Field label="Password" htmlFor="login-password">
              <div className="relative">
                <TextInput
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => set({ password: e.target.value })}
                  className="pr-11"
                  autoComplete="current-password"
                  required
                />
                <IconButton
                  label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </IconButton>
              </div>
            </Field>

            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="mt-1 w-full py-2.5"
            >
              {isLoading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-line" />
            <span className="text-xs text-ink-3">or</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <Button
            onClick={handleMicrosoftLogin}
            disabled={isMicrosoftLoading}
            className="w-full py-2.5"
          >
            <MicrosoftLogo />
            {isMicrosoftLoading ? 'Redirecting…' : 'Sign in with Microsoft'}
          </Button>
        </div>

        <p className="mt-5 text-center text-xs leading-relaxed text-ink-3">
          Accounts are created by an admin. If you can't get in, ask them to add you.
        </p>
      </div>
    </div>
  );
};

export default Login;
